import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import BrandMark from "../../components/BrandMark";
import {
  api,
  peopleApi,
  setPeopleEngineSession,
  formatDetail,
} from "../../lib/api";

/**
 * Fresh authenticated session every time. No persistent token in localStorage.
 * Logging in again is required after every tab close.
 */
export default function PeopleLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/auth/manager/login", {
        email: email.trim(),
        pin: pin.trim(),
      });
      const token = r.data.token;
      const manager = r.data.manager;

      // Hydrate capabilities for this fresh session via the dedicated
      // access endpoint — Control Room governance is the source of truth.
      setPeopleEngineSession(token, manager, manager.capabilities || []);
      const accessRes = await peopleApi.get(
        "/access/me/modules/people_engine/flat",
      );
      setPeopleEngineSession(
        token,
        manager,
        accessRes.data.capabilities || [],
      );
      nav("/people/reviews", { replace: true });
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-neutral-100 flex flex-col" data-testid="people-login-root">
      <header className="border-b border-neutral-800 bg-[#181B22]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-neutral-100" data-testid="people-back-home">
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="h-5 w-px bg-neutral-700" />
          <BrandMark size={22} inverted />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
            People&apos;s Engine
          </span>
        </div>
      </header>
      <main className="flex-1 grid grid-cols-12 max-w-6xl w-full mx-auto px-6 py-16 gap-12">
        <section className="col-span-12 md:col-span-7">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
            Authenticated workforce-decision surface
          </div>
          <h1 className="mt-4 font-heading text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]">
            Decide<br />workforce<br />exceptions.
          </h1>
          <p className="mt-6 max-w-xl text-neutral-300">
            People&apos;s Engine is where HR &amp; admin actions happen. Approve, reject,
            override, finalise payroll. Capability-gated by Control Room.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4 max-w-xl">
            <Pillar title="Capture-only" sub="Presence devices send evidence; they cannot decide." />
            <Pillar title="Capability-gated" sub="Every action requires a capability granted via Control Room." />
            <Pillar title="Append-only audit" sub="Every transition is logged immutably with actor + reason." />
            <Pillar title="Fresh session" sub="Logging out wipes the session. No persistent admin auth." />
          </div>
        </section>
        <section className="col-span-12 md:col-span-5">
          <form
            onSubmit={submit}
            className="border border-neutral-800 bg-[#181B22] p-8"
            data-testid="people-login-form"
          >
            <div className="inline-flex items-center gap-2 px-2 py-1 border border-amber-500 text-amber-300 font-mono text-[10px] uppercase tracking-wider">
              <Lock size={11} /> Fresh session required
            </div>
            <h2 className="mt-4 font-heading text-2xl font-black tracking-tighter">
              Sign in
            </h2>
            <p className="text-sm text-neutral-400 mt-2">
              Use your manager / HR credentials. Session expires on tab close.
            </p>
            <label className="mt-6 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              Email
            </label>
            <input
              data-testid="people-login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full h-12 px-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono"
              placeholder="hr@beno.com"
              required
            />
            <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              PIN
            </label>
            <input
              data-testid="people-login-pin"
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              className="mt-2 w-full h-12 px-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono tracking-[0.5em] text-lg"
              placeholder="••••"
              required
            />
            {err && (
              <div
                data-testid="people-login-error"
                className="mt-4 text-red-400 font-mono text-xs"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              data-testid="people-login-submit"
              disabled={loading || !email || !pin}
              className="mt-6 h-12 w-full bg-amber-400 text-black font-mono text-sm uppercase tracking-[0.18em] hover:bg-amber-300 disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Open People's Engine"}
            </button>
            <div className="mt-4 font-mono text-[11px] text-neutral-500">
              Need access? Ask Control Room to grant a capability.
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

function Pillar({ title, sub }) {
  return (
    <div className="border border-neutral-800 bg-[#13161C] p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">{title}</div>
      <div className="mt-2 text-sm text-neutral-300 leading-relaxed">{sub}</div>
    </div>
  );
}
